import type { ActionTree } from 'vuex'
import dayjs from 'dayjs'
import type { CustomApiError } from '@audience-republic/audience-manager-shared/api/types'
import { isErrorStatus } from '@audience-republic/audience-manager-shared/api/utils'
import type { AuthState } from './types'
import { planRegionSettingsFetch } from './utils'
import { deleteCookie, getCookie, setCookie } from '@/utils/cookie'
import type { RootState } from '@/store/modules/types'
import { getContext } from '@/utils/general'
import { fixedEncodeURIComponent } from '@/utils/helpers'

// Region settings are a presentational fallback, so a failed fetch is retried
// on the next navigation rather than latched — but only up to this many times,
// so a persistently dead endpoint cannot refetch for the whole session.
const MAX_REGION_SETTINGS_ATTEMPTS = 3

// How long a cold load waits for the region settings before painting money
// with the default symbol. Presentational, so it is a budget, not a hard
// dependency. Exported for the timeout test.
export const REGION_SETTINGS_PAINT_BUDGET_MS = 1500

// Dedupes concurrent ENSURE_REGION_SETTINGS calls. Two rapid navigations can
// both pass the "not fetched yet" check before either commit lands; without
// this they would each dispatch a fetch and each bump regionSettingsAttempts
// from the same base. Holds the single in-flight fetch and nulls itself once
// it settles. Module-level rather than in vuex state because a promise is not
// serializable state.
let regionSettingsInFlight: Promise<void> | null = null

// Tests reset the in-flight promise between cases, since a case that runs
// against the paint budget resolves before the underlying fetch settles.
export function __resetRegionSettingsInFlight(): void {
  regionSettingsInFlight = null
}

export const authActions: ActionTree<AuthState, RootState> = {
  async REGISTER_TO_WAVE({ commit }, { emailAddress, password, firstName, lastName, company, mobileNumber, country }) {
    const { $api, $arNotification } = getContext()
    try {
      commit('SET_IS_DOING_SIGNUP', true)
      const successRedirectUri = await $api.auth.registerToWaveApp({ emailAddress, password, firstName, lastName, company, mobileNumber, country })
      window.location.href = successRedirectUri
    }
    catch (e: any) {
      console.error(e)
      if (e.response && e.response.status === 409) {
        $arNotification.push({
          type: 'error',
          message: 'There is already an account with this email address,',
          link: 'Login here',
        })
      }
      else {
        $arNotification.push({
          type: 'error',
          message: 'Failed to create an account',
        })
      }
      return false
    }
    finally {
      commit('SET_IS_DOING_SIGNUP', false)
    }
  },
  async SIGNUP(
    { commit },
    { emailAddress, password, firstName, lastName, company, mobileNumber, country, companyCategory },
  ) {
    const { $axios, $arNotification } = getContext()
    const config = useRuntimeConfig()

    try {
      commit('SET_IS_DOING_SIGNUP', true)

      let pardotCookie = null

      if (config.public.arSalesforceID) {
        pardotCookie = getCookie(`visitor_id${config.public.arSalesforceID}`)
      }

      const { headers, status, data } = await $axios.post('/signup', {
        emailAddress,
        password,
        firstName,
        lastName,
        company,
        mobileNumber,
        country,
        companyCategory,
        pardotCookie,
      })

      if (status !== 201) {
        throw new Error('Failed to signup')
      }

      commit('SET_PROMOTER_ACCOUNT', data.account)
      commit('SET_X_AUTH_TOKEN', headers['x-auth-token'])

      // Store token in cookie for subdomain sharing
      setCookie('x-auth-token', headers['x-auth-token'])

      return true
    }
    catch (e: any) {
      console.error(e)
      if (e.response && e.response.status === 409) {
        $arNotification.push({
          type: 'error',
          message: 'There is already an account with this email address,',
          link: 'Login here',
        })
      }
      else {
        $arNotification.push({
          type: 'error',
          message: 'Failed to create an account',
        })
      }
      return false
    }
    finally {
      commit('SET_IS_DOING_SIGNUP', false)
    }
  },
  async LOGIN({ getters, commit }, { emailAddress, password, targetAccountEmailAddress, masquerade, mfaCode }) {
    const { $axios, $arNotification, $router } = getContext()
    const config = useRuntimeConfig()

    try {
      commit('SET_IS_DOING_LOGIN', true)

      let url
      let payload
      let pardotCookie = null

      if (config.public.arSalesforceID) {
        pardotCookie = getCookie(`visitor_id${config.public.arSalesforceID}`)
      }

      if (masquerade) {
        url = '/session-masquerade'
        payload = { emailAddress, password, targetAccountEmailAddress, pardotCookie }
      }
      else {
        url = '/session'
        payload = { emailAddress, password, pardotCookie }
      }

      if (mfaCode) {
        payload['otpToken'] = mfaCode
      }

      const { headers, status, data } = await $axios.post(url, payload)

      if (status !== 201) {
        throw new Error('Failed to login')
      }

      commit('SET_PROMOTER_ACCOUNT', data.account)
      commit('SET_SAMPLING', data.account.promoterOid === 18)
      commit('SET_X_AUTH_TOKEN', headers['x-auth-token'])

      // Store token in cookie for subdomain sharing
      setCookie('x-auth-token', headers['x-auth-token'])

      if (getters.isAdminAccount) {
        $router.push(`/admin/dashboard`)
      }
      else {
        $router.push(`/audience`)
      }
      return true
    }
    catch (e: any) {
      console.error(e)
      if (e.response && e.response.status === 401 && e.response?.data?.result === 'Missing MFA token') {
        return 'require-otp-code'
      }
      if (e.response && e.response.status === 403 && e.response?.data?.result === 'Setup MFA device') {
        $arNotification.push({ type: 'error', message: 'Finish MFA', timeout: 5000 })
        return 'mfa-setup'
      }
      if (e.response && e.response.status === 429) {
        // The server says we've tried too many times; the server will return the time to wait
        // in seconds before we can start the next request
        const { nextAllowedLoginAttemptTime } = e.response.data.message.details
        commit('SET_NEXT_LOGIN_RETRY_TIME', dayjs.utc(nextAllowedLoginAttemptTime).toDate().getTime())
        $arNotification.push({ type: 'error', message: 'Too many login attempts', timeout: 5000 })
      }
      else if (e.response && e.response.status === 402) {
        $arNotification.push({ type: 'error', message: 'Your account has been disabled, contact support@audiencerepublic.com' })
      }
      else {
        const { remainingAttempts } = e.response.data.result || {}
        $arNotification.push({ type: 'error', message: `Failed to login, remaining attempts ${remainingAttempts}` })
      }
      return false
    }
    finally {
      commit('SET_IS_DOING_LOGIN', false)
    }
  },
  async LOGOUT({ commit, rootGetters }) {
    const { $router, $store } = getContext()

    // Clear the auth cookie
    deleteCookie('x-auth-token')

    // Set secondaryNavVisible to true in localStorage
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem('secondaryNavVisible', JSON.stringify(true))
    }

    $store?.commit('RESET_STATE')
    // $store.resetState()
    $router.push({
      path: '/authenticate/login',
    })
  },
  async VERIFY_TOKEN({ commit, getters }) {
    const { $axios, $router } = getContext()
    try {
      // Start measuring the time
      const startTime = performance.now()
      console.log('Starting session verification')

      // Try to get the session data
      const { data, headers } = await $axios.get('/session')

      const requestTime = (performance.now() - startTime).toFixed(2)

      if (data.promoterOid === -100) {
        throw 'wrong token'
      }

      commit('SET_PROMOTER_ACCOUNT', data)

      const domain = window.location.hostname.split('.').slice(-2).join('.')
      const cookieOptions = {
        domain: `.${domain}`, // Add dot prefix for subdomain support
        path: '/',
        secure: window.location.protocol === 'https:',
        sameSite: 'lax', // Allow cross-subdomain usage
      }

      const hasBetaTester = !!data.features.find(f => f.id === 'beta-tester')
      // Always set explicitly ('true'/'false') so the route middleware can distinguish
      // "verified, no access" (false) from "not yet verified" (absent)
      setCookie('am3BetaTester', hasBetaTester ? 'true' : 'false', cookieOptions)

      if (getCookie('am3EarlyAccessOptIn') && !hasBetaTester) {
        // Handle reverting the beta-tester optin cookie when chargebee flag is revoked
        console.log('REMOVE am3EarlyAccessOptIn COOKIE as ACCESS TO BETA-TESTER HAS BEEN REVOKED')
        setCookie('am3EarlyAccessOptIn', '', { ...cookieOptions, maxAge: 0 })
      }

      const token = getCookie('x-auth-token')
      commit('SET_X_AUTH_TOKEN', token)

      if ($router.currentRoute.path === '/authenticate/login') {
        if (getters.isAdminAccount) {
          $router.push(`/admin/dashboard`)
        }
        else {
          $router.push(`/audience`)
        }
      }
      return true
    }
    catch (e) {
      console.error('Error verifying token:', e)

      // Check if the error is a CORS error
      if (e === 'wrong token' || e instanceof DOMException && e.name === 'NetworkError') {
        console.error('AUTH: Wrong Token : ', token)
      }
      else if (typeof e === 'object' && e !== null && 'message' in e && typeof e.message === 'string' && (
        e.message.includes('Network Error')
        || e.message.includes('CORS')
        || e.message.includes('Cross-Origin')
      )) {
        console.error('AUTH: Network error detected during session verification')
      }

      // Important, if x-auth-token is not valid, remove it otherwise it will be an infinite loop
      deleteCookie('x-auth-token')
      $router.push('/authenticate/login')

      return false
    }
  },
  async CHANGE_PASSWORD({ commit, state }, { oldPassword, password }) {
    const { $axios, $arNotification } = getContext()
    if (!state.account) { return null }
    const { promoterOid, oid } = state.account
    try {
      commit('SET_IS_CHANGING_PASSWORD', true)
      await $axios.post(`/promoter/${promoterOid}/account/${oid}/password-change`, {
        oldPassword,
        password,
      })
      $arNotification.push({
        type: 'success',
        message: 'Successfully changed password',
      })
      return true
    }
    catch (e: any) {
      console.error(e)
      if (e.response && e.response.status === 403) {
        $arNotification.push({
          type: 'error',
          message: 'Password change failed. Please ensure details are correct',
        })
      }
      else {
        $arNotification.push({
          type: 'error',
          message: 'Failed to change password',
        })
      }
      return false
    }
    finally {
      commit('SET_IS_CHANGING_PASSWORD', false)
    }
  },
  async SEND_RESET_PASSWORD_EMAIL({ commit }, emailAddress) {
    const { $axios, $arNotification } = getContext()
    try {
      commit('SET_IS_SENDING_RESET_PASSWORD_EMAIL', true)
      const { data: { nextResetRequestTime } } = await $axios.post('/request-password-reset', { emailAddress })
      // Start to count down for availability of next request
      commit('SET_NEXT_SEND_RESET_PASSWORD_EMAIL_RETRY_TIME', dayjs.utc(nextResetRequestTime).toDate().getTime())

      $arNotification.push({
        type: 'success',
        message: 'You will receive an email with instructions about how to reset your password in a few minutes.',
      })
    }
    catch (e: any) {
      console.error(e)
      if (e.response && e.response.status === 429) {
        // Edge case, if user skip our mechanism to send request before it's available
        commit('SET_NEXT_SEND_RESET_PASSWORD_EMAIL_RETRY_TIME', dayjs.utc(e.response.data.message.details.nextResetRequestTime).toDate().getTime())
      }
      else {
        $arNotification.push({
          type: 'error',
          message: 'Failed to send reset instructions',
        })
      }
    }
    finally {
      commit('SET_IS_SENDING_RESET_PASSWORD_EMAIL', false)
    }
  },
  async RESET_FORGOTTEN_PASSWORD({ commit, getters }, { password, verifyToken }) {
    const { $axios, $arNotification, $router } = getContext()
    try {
      commit('SET_IS_RESETTING_PASSWORD', true)
      const { headers, data } = await $axios.post('/password-reset', {
        password,
        verifyToken,
      })

      commit('SET_PROMOTER_ACCOUNT', data.account)
      commit('SET_X_AUTH_TOKEN', headers['x-auth-token'])

      // Store token in cookie for subdomain sharing
      setCookie('x-auth-token', headers['x-auth-token'])

      if (getters.isAdminAccount) {
        $router.push(`/admin/dashboard`)
      }
      else {
        $router.push(`/audience`)
      }
      $arNotification.push({
        type: 'success',
        message: 'Success! Your password has been reset',
      })
    }
    catch (e) {
      console.error(e)
      $arNotification.push({
        type: 'error',
        message: 'Failed to reset password',
      })
    }
    finally {
      commit('SET_IS_RESETTING_PASSWORD', false)
    }
  },
  async VERIFY_EMAIL_ADDRESS({ commit, getters }, { emailAddress, verifyToken }) {
    const { $axios, $arNotification, $router } = getContext()
    try {
      commit('SET_IS_VERIFYING_EMAIL_ADDRESS', true)
      const { headers, data } = await $axios.post('/email-verify', { emailAddress, verifyToken })

      commit('SET_PROMOTER_ACCOUNT', data.account)
      commit('SET_X_AUTH_TOKEN', headers['x-auth-token'])

      // Store token in cookie for subdomain sharing
      setCookie('x-auth-token', headers['x-auth-token'])

      // Let's always to go to message center if you're normal promoter
      if (getters.isAdminAccount) {
        $router.push(`/admin/dashboard`)
      }
      else {
        $router.push(`/message-center/messages`)
      }

      $arNotification.push({
        type: 'success',
        message: 'Success! Your email address has been verified',
      })
    }
    catch (e: any) {
      console.error(e)
      if (e.response && e.response.status === 500) {
        // It it's 500, that means unexpected error happened on server side
        $arNotification.push({
          type: 'error',
          message: 'Failed to verify your email',
        })
      }
      else if (e.response && e.response.status === 403) {
        // It it's 500, that means unexpected error happened on server side
        $arNotification.push({
          type: 'error',
          message: 'The verify token is invalid',
        })
      }
      else {
        $arNotification.push({
          type: 'error',
          message: 'Your verification link has expired. You need to log and resend the verification email',
          timeout: 10000,
        })
        $router.push({
          path: '/authenticate/login',
          query: { emailAddress },
        })
      }
    }
    finally {
      commit('SET_IS_VERIFYING_EMAIL_ADDRESS', false)
    }
  },

  // Fetch email send limit settings
  async FETCH_EMAIL_SEND_LIMIT_SETTINGS(
    { commit, state },
  ) {
    if (!state.account) return
    const { promoterOid } = state.account
    const { $api, $arNotification } = getContext()
    try {
      const data = await $api.promoterProperty.get(promoterOid, 'promoter-email-send-limit-settings')
      commit('SET_EMAIL_SEND_LIMIT_SETTINGS', data)
      return data
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to fetch email send limit settings',
      })
    }
  },
  // Create email send limit settings
  async CREATE_EMAIL_SEND_LIMIT_SETTINGS(
    { commit, state },
    { property },
  ) {
    if (!state.account) return
    const { promoterOid } = state.account
    const { $api, $arNotification } = getContext()
    try {
      commit('SET_IS_UPDATING_EMAIL_SEND_LIMIT_SETTINGS', true)
      const data = await $api.promoterProperty.post(promoterOid, {
        type: 'promoter-email-send-limit-settings',
        property: property.defaultTimezone,
        allowMany: false,
        promoterOid,
        additional_info: property,
      })
      commit('SET_EMAIL_SEND_LIMIT_SETTINGS', data)
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to save email send limit settings',
      })
    }
    finally {
      $arNotification.push({
        type: 'success',
        message: 'Successfully saved email send limit settings',
      })
      commit('SET_IS_UPDATING_EMAIL_SEND_LIMIT_SETTINGS', false)
    }
  },
  // Update email send limit settings
  async UPDATE_EMAIL_SEND_LIMIT_SETTINGS(
    { commit, state },
    { propertyOid, property },
  ) {
    if (!state.account) return
    const { promoterOid } = state.account
    const { $api, $arNotification } = getContext()
    try {
      commit('SET_IS_UPDATING_EMAIL_SEND_LIMIT_SETTINGS', true)
      const data = await $api.promoterProperty.patch(promoterOid, propertyOid, {
        type: 'promoter-email-send-limit-settings',
        property: property.defaultTimezone,
        allowMany: false,
        promoterOid,
        additional_info: property,
      })
      commit('SET_EMAIL_SEND_LIMIT_SETTINGS', data)
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to save email send limit settings',
      })
    }
    finally {
      $arNotification.push({
        type: 'success',
        message: 'Successfully saved email send limit settings',
      })
      commit('SET_IS_UPDATING_EMAIL_SEND_LIMIT_SETTINGS', false)
    }
  },

  // Fetch Promoter region settings
  // `silent` suppresses the toast for callers the user did not initiate. The
  // app bootstrap dispatches this for a presentational fallback, and a
  // transient failure there should not put an error in front of someone who
  // just logged in.
  async FETCH_REGION_SETTINGS(
    { commit, state },
    { silent = false } = {},
  ) {
    if (!state.account) return
    const { promoterOid } = state.account
    const { $api, $arNotification } = getContext()

    // The latch records "we have an answer", not "we tried". Latching on
    // failure too would make one transient 5xx at bootstrap permanent: nothing
    // clears it short of RESET_STATE on logout, so a GBP promoter would see '$'
    // on every tile, chart and activity line for the rest of the session.
    // Leaving it unlatched lets the next navigation recover, and the attempt
    // cap stops a persistently dead endpoint from refetching forever.
    if (state.regionSettingsAttempts >= MAX_REGION_SETTINGS_ATTEMPTS) {
      commit('SET_HAS_FETCHED_REGION_SETTINGS', true)
      return
    }

    commit('SET_REGION_SETTINGS_ATTEMPTS', state.regionSettingsAttempts + 1)

    try {
      const data = await $api.promoterProperty.get(promoterOid, 'promoter-region-settings')

      // A null `data` is a real answer — most promoters have never saved any —
      // so this latches on success even when there is nothing to store.
      commit('SET_REGION_SETTINGS', data)
      commit('SET_HAS_FETCHED_REGION_SETTINGS', true)
    }
    catch (error) {
      console.error(error)
      if (!silent) {
        $arNotification.push({
          type: 'error',
          message: 'Failed to fetch promoter region settings',
        })
      }
    }
  },
  // The auth middleware's entry point for loading region settings. Owns the
  // gating decision (delegated to planRegionSettingsFetch so it is testable
  // outside the middleware, which vitest excludes), the concurrent-call dedupe,
  // and the cold-load paint budget — so the middleware is a thin caller.
  async ENSURE_REGION_SETTINGS(
    { dispatch, getters, state },
    { isColdLoad = false } = {},
  ) {
    const { shouldDispatch, shouldAwait } = planRegionSettingsFetch({
      promoterOid: getters.userPromoterOid,
      hasFetched: state.hasFetchedRegionSettings,
      isColdLoad,
    })

    if (!shouldDispatch) return

    // Reuse an in-flight fetch rather than starting a second. Errors are
    // swallowed here because FETCH_REGION_SETTINGS already reports them (subject
    // to `silent`); this wrapper only cares that the attempt has settled.
    regionSettingsInFlight ??= (dispatch('FETCH_REGION_SETTINGS', { silent: true }) as Promise<void>)
      .catch(() => {})
      .finally(() => { regionSettingsInFlight = null })

    const fetching = regionSettingsInFlight

    // A cold load waits, but only for the budget: the middleware's finally
    // releases every API request queued behind it, so a slow property read must
    // not hold those up. Past the budget the reactive symbol update takes over.
    if (shouldAwait) {
      await Promise.race([
        fetching,
        new Promise(resolve => setTimeout(resolve, REGION_SETTINGS_PAINT_BUDGET_MS)),
      ])
    }
  },
  // Create new Promoter region settings
  async CREATE_REGION_SETTINGS(
    { commit, state },
    { property },
  ) {
    if (!state.account) return
    const { promoterOid } = state.account
    const { $api, $arNotification } = getContext()

    try {
      commit('SET_IS_UPDATING_REGION_SETTINGS', true)

      const data = await $api.promoterProperty.post(promoterOid, {
        type: 'promoter-region-settings',
        property: property.defaultTimezone,
        allowMany: false,
        promoterOid,
        additionalInfo: property,
      })

      commit('SET_REGION_SETTINGS', data)
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to create new promoter region settings',
      })
    }
    finally {
      $arNotification.push({
        type: 'success',
        message: 'Successfully updated region settings',
      })
      commit('SET_IS_UPDATING_REGION_SETTINGS', false)
    }
  },
  // Update new Promoter region settings
  async UPDATE_REGION_SETTINGS(
    { commit, state },
    { propertyOid, property },
  ) {
    if (!state.account) return
    const { promoterOid } = state.account
    const { regionSettings } = state
    const { $api, $arNotification } = getContext()

    try {
      commit('SET_IS_UPDATING_REGION_SETTINGS', true)

      const data = await $api.promoterProperty.patch(promoterOid, propertyOid, {
        type: 'promoter-region-settings',
        property: property.defaultTimezone,
        allowMany: false,
        promoterOid,
        additionalInfo: property,
      })

      // Set the store manually because patch doesn't return anything
      const newRegionSettings = {
        ...regionSettings,
        property: property.defaultTimezone,
        additionalInfo: {
          ...regionSettings.additionalInfo,
          defaultTimezone: property.defaultTimezone,
          currency: property.currency,
        },
      }

      commit('SET_REGION_SETTINGS', newRegionSettings)
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to create new promoter region settings',
      })
    }
    finally {
      $arNotification.push({
        type: 'success',
        message: 'Successfully updated region settings',
      })
      commit('SET_IS_UPDATING_REGION_SETTINGS', false)
    }
  },

  async FETCH_PROMOTER(
    { rootState, state, commit },
  ) {
    if (!rootState.authModule.account) return
    const { promoterOid } = rootState.authModule.account
    const { $arNotification, $axios } = getContext()

    try {
      const { data } = $axios.get(`/promoter/${promoterOid}`)

      commit('SET_PROMOTER', data)
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to fetch promoter',
      })
    }
  },

  async FETCH_PROMOTER_ACCOUNTS(
    { rootState, commit },
    { top = 20, skip = 0, searchString = '', orderByString = 'oid%20asc', arepAdminOid = null },
  ) {
    if (!rootState.authModule.account) return
    const { promoterOid } = rootState.authModule.account
    const { $axios } = getContext()

    const selectString = 'firstName,lastName,emailAddress,sysCtime,promoterOid,defaultUserRoleOid,promoterUserRoleOid'
    const filterString = fixedEncodeURIComponent(`sysActivep AND (emailAddress ILIKE "%${searchString}%" OR firstName ILIKE "%${searchString}%" OR emailAddress ILIKE "%${searchString}%" OR firstName ILIKE "%${searchString}%")`)
    const arepAdminFilter = arepAdminOid ? fixedEncodeURIComponent(` AND defaultUserRoleOid != ${arepAdminOid}`) : ''

    try {
      commit('SET_IS_FETCHING_USERS', true)
      const { data } = await $axios.get(`/promoter/${promoterOid}/account?$select=${selectString}&$top=${top}&$skip=${skip}&$orderby=${orderByString}&$filter=${filterString}${arepAdminFilter}`)

      commit('SET_USERS', data)
    }
    catch (error) {
      console.error(error)
    }
    finally {
      commit('SET_IS_FETCHING_USERS', false)
    }
  },
  async FETCH_INVITEES(
    { rootState, commit },
  ) {
    if (!rootState.authModule.account) return
    const { promoterOid } = rootState.authModule.account
    if (!promoterOid) return
    const { $api } = getContext()
    try {
      commit('SET_IS_FETCHING_INVITEES', true)
      const { data, status } = await $api.auth.fetchInvitees(promoterOid)
      commit('SET_INVITEES', data)
    }
    catch (e: any) {
      console.error(e)
    }
    finally {
      commit('SET_IS_FETCHING_INVITEES', false)
    }
  },
  async DELETE_INVITE(
    { rootState, commit },
    { inviteOid },
  ) {
    if (!rootState.authModule.account) return
    const { promoterOid } = rootState.authModule.account
    if (!promoterOid) return
    if (!inviteOid) return
    const { $api } = getContext()

    try {
      const { data, status } = await $api.auth.deleteUserInvite(promoterOid, inviteOid)
      return data
    }
    catch (e) {
      console.error(e)
    }
  },
  // Default user roles
  async FETCH_DEFAULT_USER_ROLES(
    { rootState, commit },
    { searchString = null },
  ) {
    if (!rootState.authModule.account) return
    const { $arNotification, $axios } = getContext()

    try {
      const { data, status } = await $axios.get(`/user-role`, {
        params: {
          $filter: searchString ? `name ILIKE "%${searchString}%" AND name != "AREP Admin"` : null,
        },
      })
      if (isErrorStatus(status)) {
        const apiError: CustomApiError = {
          name: 'Fetching default user roles error',
          message: `Error fetching default user roles`,
          status,
        }
        throw apiError
      }

      commit('SET_DEFAULT_USER_ROLES', data)
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to fetch default user roles',
      })
    }
  },
  // Promoter user roles
  async FETCH_PROMOTER_USER_ROLES(
    { rootState, commit },
    { searchString = null },
  ) {
    if (!rootState.authModule.account) return
    const { promoterOid } = rootState.authModule.account
    const { $arNotification, $axios } = getContext()

    try {
      const { data, status } = await $axios.get(`/promoter/${promoterOid}/user-role`, {
        params: {
          $filter: searchString ? `name ILIKE "%${searchString}%"` : null,
        },
      })
      if (isErrorStatus(status)) {
        const apiError: CustomApiError = {
          name: 'Fetching promoter user roles error',
          message: `Error fetching promoter roles for OID ${promoterOid}`,
          status,
        }
        throw apiError
      }

      commit('SET_PROMOTER_USER_ROLES', data)
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to fetch promoter user roles',
      })
    }
  },
  async UPDATE_PROMOTER_ACCOUNT(
    { rootState, dispatch, commit },
    { accountOid, promoterAccountObj },
  ) {
    if (!rootState.authModule.account) return
    const { promoterOid } = rootState.authModule.account
    const { $arNotification, $axios } = getContext()

    try {
      const updatedData = {
        firstName: promoterAccountObj.firstName,
        lastName: promoterAccountObj.lastName,
        defaultUserRoleOid: promoterAccountObj.defaultUserRoleOid,
        promoterUserRoleOid: promoterAccountObj.promoterUserRoleOid,
      }
      await $axios.patch(`/promoter/${promoterOid}/manage-account/${accountOid}`, updatedData)
      commit('SET_PROMOTER_ACCOUNT_DATA', {
        ...updatedData,
        oid: accountOid,
      })
      return true
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to update account',
      })
      return false
    }
  },
  async DELETE_PROMOTER_ACCOUNT(
    { rootState, commit },
    { accountOid },
  ) {
    if (!rootState.authModule.account) return
    const { promoterOid } = rootState.authModule.account
    const { $arNotification, $axios } = getContext()

    try {
      await $axios.delete(`/promoter/${promoterOid}/manage-account/${accountOid}`)
      $arNotification.push({
        type: 'success',
        message: 'Account has been deleted',
      })
      commit('DELETE_PROMOTER_ACCOUNT', accountOid)
      return true
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to delete account',
      })
      return false
    }
  },
  async SEND_ACCOUNT_INVITE({ commit, getters, state }, { emailAddress, defaultUserRoleOid, promoterUserRoleOid }) {
    if (!state.account) return null
    const { promoterOid } = state.account
    if (!promoterOid) return null
    const { $arNotification, $api } = getContext()
    try {
      const isSent = await $api.auth.sendUserInvite({
        promoterOid,
        emailAddress,
        defaultUserRoleOid,
        promoterUserRoleOid,
      })
      if (isSent) {
        $arNotification.push({
          type: 'success',
          message: `An invitation has been sent to ${emailAddress}`,
        })
      }
      else {
        $arNotification.push({
          type: 'error',
          message: 'Failed to send invite',
        })
      }
    }
    catch (e: any) {
      console.error(e)
      const internalErrorCode = e?.response?.data['internal-code']
      const internalErrorDetails = e?.response?.data?.message?.details
      const internalErrorSummary = e?.response?.data?.message?.summary

      if (internalErrorCode === 'record-exists') {
        $arNotification.push({
          type: 'error',
          message: `Failed to send invite - ${internalErrorDetails}`,
        })
        return
      }
      $arNotification.push({
        type: 'error',
        message: `Failed to send invite - ${internalErrorSummary}`,
      })
    }
  },
  // Fetch and populate permissions for the currently logged in promoter_account
  async FETCH_PERMISSIONS({ commit, getters, state }) {
    if (!state.account) { return null }
    const { promoterOid } = state.account
    const { $arNotification, $api } = getContext()

    try {
      const data = await $api.auth.fetchPromoterAccountPermissions(promoterOid)
      commit('SET_PERMISSIONS', data)
      return data
    }
    catch (e: any) {
      console.error(e)
      $arNotification.push({
        type: 'error',
        message: 'Failed to fetch permissions',
      })
    }
  },
  async FINISH_INVITE({ commit, state, getters }, validationField) {
    if (state.isFinishingInvite) {
      return false
    }

    const { $arNotification, $api, $router } = getContext()
    try {
      commit('SET_IS_FINISHING_INVITE', true)
      const session = await $api.auth.finishInvite(validationField)

      commit('SET_PROMOTER_ACCOUNT', session.account)
      commit('SET_X_AUTH_TOKEN', session.accessToken)

      // Store token in cookie for subdomain sharing
      setCookie('x-auth-token', session.accessToken)

      if ($router.currentRoute.value.path === '/authenticate/invite') {
        if (getters.isAdminAccount) {
          $router.push(`/admin/dashboard`)
        }
        else {
          $router.push(`/audience`)
        }
      }

      return session
    }
    catch (e: any) {
      console.error(e)
      // If the invited user hasn't finished registration successfully, we need to clear the cookie
      deleteCookie('x-auth-token')

      console.error(e)
      $arNotification.push({
        type: 'error',
        message: 'We are unable to complete registration.',
        timeout: 10000,
      })

      return false
    }
    finally {
      commit('SET_IS_FINISHING_INVITE', false)
    }
  },
  async VALIDATE_INVITE({ commit, state }, validationField) {
    const { $arNotification, $api } = getContext()
    try {
      commit('SET_IS_VALIDATING_INVITE', true)
      const data = await $api.auth.validateInvite(validationField)
      return data
    }
    catch (e) {
      console.error(e)
      $arNotification.push({
        type: 'error',
        message: 'Your invitation link is no longer valid.',
        timeout: 10000,
      })
      return false
    }
    finally {
      commit('SET_IS_VALIDATING_INVITE', false)
    }
  },
  async RESEND_INVITE({ commit, state }, email) {
    const { $arNotification, $api } = getContext()
    if (!email) {
      console.error('No email present - cannot resend email')
      $arNotification.push({
        type: 'error',
        message: `Invite cannot be resent`,
        timeout: 10000,
      })
      return
    }
    try {
      commit('SET_IS_VALIDATING_INVITE', true)
      const response = await $api.auth.resendInvite(email)

      return true
    }
    catch (e: any) {
      if (e.response.status === 429) {
        $arNotification.push({
          type: 'error',
          message: `Invite cannot be resent for ${email}. Try again after 5 minutes.`,
          timeout: 10000,
        })
        return false
      }
      else if (e.response.status === 404) {
        $arNotification.push({
          type: 'error',
          message: `Cannot find a matching invite. Please refresh the page and try again.`,
          timeout: 10000,
        })
        return false
      }
      else {
        $arNotification.push({
          type: 'error',
          message: e.message || `Cannot resend the invite to ${email}.`,
          timeout: 10000,
        })
      }

      return false
    }
    finally {
      commit('SET_IS_VALIDATING_INVITE', false)
    }
  },

  async FETCH_TWILIO_SUBACCOUNT(
    { rootState, state, commit },
  ) {
    if (!rootState.authModule.account) { return null }
    const { promoterOid } = state.account

    commit('SET_IS_FETCHING_TWILIO_SUBACCOUNT', true)
    const { $api } = getContext()
    try {
      const data = await $api.twilioSubAccounts.getSubaccount(promoterOid)

      commit('SET_TWILIO_SUBACCOUNT', data)

      return data
    }
    catch (error) {
      console.error(error)
    }
    finally {
      commit('SET_IS_FETCHING_TWILIO_SUBACCOUNT', false)
    }
  },
  async FETCH_ALPHANUMERIC_SENDER(
    { rootState, state, commit },
  ) {
    if (!rootState.authModule.account) { return null }
    const { promoterOid } = state.account

    commit('SET_IS_FETCHING_SMS_SENDER_IDS', true)
    const { $api } = getContext()
    try {
      const data = await $api.twilioSenderIds.get(promoterOid)

      commit('SET_SMS_SENDER_IDS', data)

      return data
    }
    catch (error) {
      console.error(error)
    }
    finally {
      commit('SET_IS_FETCHING_SMS_SENDER_IDS', false)
    }
  },
  async FETCH_DOMAINS(
    { rootState, state, commit },
  ) {
    if (!rootState.authModule.account) { return null }
    const { promoterOid } = state.account

    commit('SET_IS_FETCHING_DOMAINS', true)
    const { $api } = getContext()
    try {
      const data = await $api.auth.fetchDomains(promoterOid)

      commit('SET_DOMAINS', data)

      return data
    }
    catch (error) {
      console.error(error)
    }
    finally {
      commit('SET_IS_FETCHING_DOMAINS', false)
    }
  },
  async DELETE_DOMAIN(
    { rootState, state, commit },
    { domainOid },
  ) {
    if (!rootState.authModule.account) { return null }
    if (!domainOid) return null
    const { promoterOid } = state.account
    const { $api } = getContext()

    try {
      const deleted = await $api.auth.deleteDomainAuth(promoterOid, domainOid)

      const data = await $api.auth.fetchDomains(promoterOid)
      commit('SET_DOMAINS', data)

      return data
    }
    catch (error) {
      console.error(error)
    }
  },
  // Create new domain authentication
  async CREATE_DOMAIN_AUTH(
    { commit, state },
    { domain },
  ) {
    if (!state.account) return
    const { promoterOid } = state.account
    const { $arNotification, $api } = getContext()

    try {
      commit('SET_IS_CREATING_DOMAIN_AUTH', true)

      const data = await $api.auth.createDomainAuth({
        promoterOid,
        domain,
      })

      commit('ADD_TO_DOMAINS', data)

      return data
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to create new domain authentication',
      })
    }
    finally {
      $arNotification.push({
        type: 'success',
        message: 'Successfully created domain authentication',
      })
      commit('SET_IS_CREATING_DOMAIN_AUTH', false)
    }
  },
  // Create new domain authentication
  async VERIFY_DOMAIN_AUTH(
    { commit, state },
    { domain },
  ) {
    if (!state.account) return
    const { promoterOid } = state.account
    const { $arNotification, $api } = getContext()

    try {
      commit('SET_IS_VALIDATING_DOMAIN', true)
      const data = await $api.auth.verifyDomainAuth({
        promoterOid,
        domain,
      })
      return data
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to verify new domain authentication',
      })
    }
    finally {
      commit('SET_IS_VALIDATING_DOMAIN', false)
      $arNotification.push({
        type: 'success',
        message: 'Domain submitted, verification may take up to 48 hours',
      })
    }
  },
  // Get Time to Value data
  async FETCH_TIME_TO_VALUE(
    { rootState, state, commit },
  ) {
    if (!rootState.authModule.account) { return null }
    const { promoterOid } = state.account

    commit('SET_IS_FETCHING_TIME_TO_VALUE', true)
    const { $api } = getContext()
    try {
      const data = await $api.auth.fetchTimeToValue(promoterOid)

      commit('SET_TIME_TO_VALUE', data)

      return data
    }
    catch (error) {
      console.error(error)
    }
    finally {
      commit('SET_IS_FETCHING_TIME_TO_VALUE', false)
    }
  },
  // Create new domain authentication
  async CREATE_TIME_TO_VALUE(
    { commit, state },
    { column },
  ) {
    if (!state.account) return
    const { promoterOid } = state.account
    const { $arNotification, $api } = getContext()

    try {
      const data = await $api.auth.createTimeToValue(
        promoterOid,
        column,
      )
      return data
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to create or update Time to Value item',
      })
    }
    finally {
      $arNotification.push({
        type: 'success',
        message: 'Time to Value successfully created/updated',
      })
    }
  },

  async SET_MFA_FORCE({}, { promoterOid, promoterAccountOid, value }) {
    const { $arNotification, $api } = getContext()

    try {
      await $api.auth.requestSetMfaForceForPromoter(promoterOid, promoterAccountOid, value)
      $arNotification.push({ type: 'success', message: 'MFA force settings saved' })
      return true
    }
    catch (e) {
      $arNotification.push({ type: 'error', message: 'Failed to save MFA force settings' })
      console.error(e)
      return false
    }
  },

  async DISABLE_MFA_FOR_PROMOTER({}, { promoterOid, promoterAccountOid }) {
    const { $arNotification, $api } = getContext()

    try {
      await $api.auth.deleteMfaForPromoter(promoterOid, promoterAccountOid)
      $arNotification.push({ type: 'success', message: 'MFA disabled' })
      return true
    }
    catch (e) {
      $arNotification.push({ type: 'error', message: 'Failed to disable MFA' })
      console.error(e)
      return false
    }
  },

  async DISABLE_MFA({ rootState, state }, { otp }) {
    if (!state.account) return
    const { $arNotification, $api } = getContext()

    const { promoterOid, oid } = state.account

    try {
      await $api.auth.deleteMfa(promoterOid, oid, otp)
      $arNotification.push({ type: 'success', message: 'MFA disabled' })
      return true
    }
    catch (e) {
      $arNotification.push({ type: 'error', message: 'Failed to disable MFA' })
      console.error(e)
      return false
    }
  },

  async FETCH_MFA_CONFIGURE({ rootState, state, commit }) {
    if (!state.account) return
    const { $arNotification, $api } = getContext()
    const { promoterOid } = state.account

    try {
      const data = await $api.auth.getMfaConfigure(promoterOid)
      return data
    }
    catch (e) {
      $arNotification.push({ type: 'error', message: 'Failed to get QR' })
      console.error(e)
      return false
    }
  },

  async SET_MFA_CONFIGURE({ rootState, state, commit }, { otp1, otp2 }) {
    if (!state.account) return
    const { $arNotification, $api } = getContext()
    const { promoterOid } = state.account

    try {
      const data = await $api.auth.setMfaConfigure(promoterOid, otp1, otp2)
      $arNotification.push({ type: 'success', message: 'MFA enabled' })
      return data
    }
    catch (e) {
      $arNotification.push({ type: 'error', message: 'Failed to enable MFA for this promoter. Please contact support@audiencerepublic.com for assistance.' })
      console.error(e)
      return false
    }
  },
}
